import React, { useEffect, useMemo, useState } from 'react';
import { IconPlus, IconTrash, IconX } from './Icons';
import './CustomMultistratBuilderModal.css';

const WEEKDAYS = [
  { key: 'monday', short: 'Mon' },
  { key: 'tuesday', short: 'Tue' },
  { key: 'wednesday', short: 'Wed' },
  { key: 'thursday', short: 'Thu' },
  { key: 'friday', short: 'Fri' },
];

const MAX_NAME_LENGTH = 80;
const MAX_SLEEVES = 12;

function makeEmptySleeve() {
  return { strategy: '', days: [], size: 1 };
}

/**
 * Pro-only Builder for a "Custom Multi-Strat" combo. The user picks N
 * strategies, checks which weekdays each sleeve contributes to, sets a size
 * multiplier per sleeve, names the combo, and saves. The result becomes a
 * tab in Vector Results with the combined data.
 *
 * Props:
 *   isOpen        boolean
 *   onClose       () => void
 *   onSave        (payload) => Promise — payload = { name, sleeves }
 *                (contract type is selected per-view on Vector Results, not
 *                stored on the combo)
 *   onDelete      () => Promise — only present when editing
 *   existing      optional combo to pre-fill (edit mode)
 *   strategies    array of strategy names the user can pick from
 *   saving        boolean — disables footer buttons during request
 *   error         string|null — shown above the footer on save failure
 */
function CustomMultistratBuilderModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  existing,
  strategies = [],
  saving = false,
  error = null,
}) {
  const editMode = !!existing;

  const [name, setName] = useState('');
  const [sleeves, setSleeves] = useState([makeEmptySleeve()]);
  const [localError, setLocalError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Reset state whenever the modal opens (covers both create and edit). Keying
  // off `existing?.id` means switching from edit one combo → edit another
  // without closing in between still resets correctly.
  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setName(existing.name || '');
      const incoming = Array.isArray(existing.sleeves) ? existing.sleeves : [];
      setSleeves(
        incoming.length > 0
          ? incoming.map((s) => ({
            strategy: s?.strategy || '',
            days: Array.isArray(s?.days) ? [...s.days] : [],
            size: Number.isFinite(Number(s?.size)) ? Number(s.size) : 1,
          }))
          : [makeEmptySleeve()],
      );
    } else {
      setName('');
      setSleeves([makeEmptySleeve()]);
    }
    setLocalError(null);
    setConfirmingDelete(false);
  }, [isOpen, existing]);

  // Filter the strategy dropdown options — only real strategies pulled from
  // the user's Vector Results sheet are eligible as combo sleeves. Exclude:
  //   - the demo entry,
  //   - the synthetic Vector Master Strat / legacy Multi-Strat names
  //     (combining one virtual sheet with another is confusing + the
  //     server can't fan-out-of-a-fan-out),
  //   - existing custom combos themselves (`custom:<id>`), so users can't
  //     nest a combo inside another combo.
  const strategyOptions = useMemo(
    () => (strategies || []).filter(
      (s) => s
        && s !== 'Sample Strategy (Demo)'
        && s !== 'Multi-Strat'
        && s !== 'Vector Master Strat'
        && !(typeof s === 'string' && s.startsWith('custom:')),
    ),
    [strategies],
  );

  if (!isOpen) return null;

  const updateSleeve = (idx, patch) => {
    setSleeves((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    setLocalError(null);
  };

  const toggleDay = (idx, dayKey) => {
    setSleeves((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const has = s.days.includes(dayKey);
      return { ...s, days: has ? s.days.filter((d) => d !== dayKey) : [...s.days, dayKey] };
    }));
    setLocalError(null);
  };

  const addSleeve = () => {
    setSleeves((prev) => (prev.length >= MAX_SLEEVES ? prev : [...prev, makeEmptySleeve()]));
  };

  const removeSleeve = (idx) => {
    setSleeves((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const validate = () => {
    const trimmed = name.trim();
    if (!trimmed) return 'Give the combo a name.';
    const filled = sleeves.filter((s) => s.strategy && s.days.length > 0);
    if (filled.length === 0) {
      return 'Add at least one strategy with at least one active weekday.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validation = validate();
    if (validation) {
      setLocalError(validation);
      return;
    }
    setLocalError(null);
    const payload = {
      name: name.trim().slice(0, MAX_NAME_LENGTH),
      sleeves: sleeves
        .filter((s) => s.strategy && s.days.length > 0)
        .map((s) => {
          // Clamp to integer 1-100. Empty / NaN / fractional values default
          // to 1× rather than failing the save outright.
          const raw = Number(s.size);
          const intSize = Number.isFinite(raw) && raw >= 1
            ? Math.min(Math.max(Math.round(raw), 1), 100)
            : 1;
          return { strategy: s.strategy, days: s.days, size: intSize };
        }),
    };
    if (typeof onSave === 'function') {
      await onSave(payload);
    }
  };

  const handleDelete = async () => {
    if (!editMode || typeof onDelete !== 'function') return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    await onDelete();
  };

  const errorMessage = error || localError;

  return (
    <div className="custom-multistrat-modal-overlay" role="dialog" aria-modal="true">
      <div className="custom-multistrat-modal">
        <div className="custom-multistrat-modal-header">
          <div>
            <h3>{editMode ? 'Edit Custom Multi-Strat' : 'Build a Custom Multi-Strat'}</h3>
            <p className="custom-multistrat-modal-sub">
              Pick which strategies trade on which weekdays. Each cell&apos;s P&amp;L is summed
              across every active sleeve, scaled by that sleeve&apos;s size multiplier.
            </p>
          </div>
          <button
            type="button"
            className="custom-multistrat-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconX size={18} />
          </button>
        </div>

        <form className="custom-multistrat-modal-body" onSubmit={handleSubmit}>
          <label className="custom-multistrat-field">
            <span>Combo name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setLocalError(null); }}
              maxLength={MAX_NAME_LENGTH}
              placeholder="e.g. My GFPB + Scarberia stack"
              autoFocus
            />
          </label>

          {/* Hedging disclaimer — combining strategies that take opposite
              positions on the same day cancels P&L instead of stacking it.
              Worth flagging before the user spends time building a combo. */}
          <div className="custom-multistrat-hedging-notice" role="note">
            <strong>Heads up:</strong> Some strategies hedge each other (one
            buys while another sells the same instrument). Combining them on
            the same weekday can flatten your P&amp;L instead of stacking it.
            Double-check the source strategies before relying on a combo to
            size live trades.
          </div>

          <div className="custom-multistrat-matrix">
            <div className="custom-multistrat-matrix-header">
              <div className="custom-multistrat-col-strategy">Strategy</div>
              {WEEKDAYS.map((d) => (
                <div key={d.key} className="custom-multistrat-col-day">{d.short}</div>
              ))}
              <div className="custom-multistrat-col-size" title="Default Contract Size Multiplier">
                Default Contract Size Multiplier
              </div>
              <div className="custom-multistrat-col-remove" aria-hidden="true" />
            </div>

            {sleeves.map((sleeve, idx) => (
              <div key={idx} className="custom-multistrat-row">
                <div className="custom-multistrat-col-strategy">
                  <select
                    value={sleeve.strategy}
                    onChange={(e) => updateSleeve(idx, { strategy: e.target.value })}
                    aria-label={`Sleeve ${idx + 1} strategy`}
                  >
                    <option value="">Select strategy…</option>
                    {strategyOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="custom-multistrat-row-controls">
                {WEEKDAYS.map((d) => (
                  <div key={d.key} className="custom-multistrat-col-day">
                    <label
                      className={`custom-multistrat-day-cell ${sleeve.days.includes(d.key) ? 'is-active' : ''}`}
                      title={`${sleeve.strategy || 'this sleeve'} ${sleeve.days.includes(d.key) ? 'trades' : 'does not trade'} on ${d.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={sleeve.days.includes(d.key)}
                        onChange={() => toggleDay(idx, d.key)}
                        aria-label={`${d.short} for sleeve ${idx + 1}`}
                      />
                      <span className="custom-multistrat-day-mark" aria-hidden="true">
                        {sleeve.days.includes(d.key) ? '✓' : ''}
                      </span>
                    </label>
                    <span className="custom-multistrat-day-abbr" aria-hidden="true">{d.short}</span>
                  </div>
                ))}
                <div className="custom-multistrat-col-size">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={sleeve.size}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        updateSleeve(idx, { size: '' });
                        return;
                      }
                      // Force whole numbers in the integer 1-100 range as the
                      // user types; non-numeric stays empty until they hit
                      // something valid.
                      const n = parseInt(raw, 10);
                      if (!Number.isFinite(n)) return;
                      updateSleeve(idx, { size: Math.min(Math.max(n, 1), 100) });
                    }}
                    aria-label={`Sleeve ${idx + 1} default contract size multiplier`}
                  />
                  <span className="custom-multistrat-size-suffix">×</span>
                </div>
                <div className="custom-multistrat-col-remove">
                  <button
                    type="button"
                    className="custom-multistrat-row-remove"
                    onClick={() => removeSleeve(idx)}
                    disabled={sleeves.length === 1}
                    aria-label={`Remove sleeve ${idx + 1}`}
                    title={sleeves.length === 1 ? 'At least one sleeve required' : 'Remove this sleeve'}
                  >
                    <IconTrash size={14} />
                  </button>
                </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="custom-multistrat-add-row"
              onClick={addSleeve}
              disabled={sleeves.length >= MAX_SLEEVES}
              title={sleeves.length >= MAX_SLEEVES ? `Max ${MAX_SLEEVES} sleeves` : 'Add another strategy'}
            >
              <IconPlus size={14} /> Add another strategy
            </button>
          </div>

          {errorMessage ? (
            <div className="custom-multistrat-modal-error" role="alert">{errorMessage}</div>
          ) : null}

          <div className="custom-multistrat-modal-footer">
            {editMode && typeof onDelete === 'function' ? (
              <button
                type="button"
                className={`custom-multistrat-modal-btn custom-multistrat-modal-btn-danger ${confirmingDelete ? 'is-confirming' : ''}`}
                onClick={handleDelete}
                disabled={saving}
              >
                {confirmingDelete ? 'Click again to confirm delete' : 'Delete combo'}
              </button>
            ) : (
              <span />
            )}
            <div className="custom-multistrat-modal-footer-right">
              <button
                type="button"
                className="custom-multistrat-modal-btn"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="custom-multistrat-modal-btn custom-multistrat-modal-btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving…' : editMode ? 'Save changes' : 'Save combo'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomMultistratBuilderModal;
